import { IconButton, RadioButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { FormEventHandler, FunctionComponent, useEffect, useRef, useState } from "react"
import { CustomFieldType } from "@nova/schemas"
import { FieldProps } from "./CustomFieldBuilderTypes"
import { Input } from "@nova/ds-component-input"

const LabelInput = ({
    value,
    autoFocus,
    onChange,
}: {
    value: string
    autoFocus: boolean
    onChange: (val: string) => void
}) => {
    return (
        <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter a label"
            autoFocus={autoFocus}
        />
    )
}

const FieldFooter = ({ spec, fieldControls }: FieldProps) => {
    return (
        <div className="px-8 py-4 flex items-center gap-4">
            <div className="grow" />
            <IconButton onClick={() => fieldControls.deleteField(spec.id)}>
                <Icons.Icon
                    Icon={Icons.Trash}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                />
            </IconButton>
        </div>
    )
}

const CustomTextField = ({ spec, fieldControls, autoFocus = false }: FieldProps<"text">) => {
    return (
        <LabelInput
            value={spec.label}
            autoFocus={autoFocus}
            onChange={(label) => fieldControls.editField(spec.id, { ...spec, label })}
        />
    )
}

const CustomYesNoField = ({ spec, fieldControls, autoFocus = false }: FieldProps<"yes-no">) => {
    return (
        <LabelInput
            value={spec.label}
            autoFocus={autoFocus}
            onChange={(label) => fieldControls.editField(spec.id, { ...spec, label })}
        />
    )
}

const OptionInput = ({
    initialValue,
    onCancel,
    onSubmit,
}: {
    initialValue: string
    onSubmit: (val: string) => void
    onCancel: () => void
}) => {
    const [inputValue, setInputValue] = useState(initialValue)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
    }, [])

    const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault()
        onSubmit(inputValue)
    }

    return (
        <form onSubmit={handleSubmit} className="flex gap-1 border-b border-b-gray-700">
            <input
                ref={inputRef}
                className="bg-transparent p-0 text-gray-0 border-none focus:outline-none text-[14px]"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onBlur={() => onSubmit(inputValue)}
            />
            <IconButton onClick={() => onSubmit(inputValue)}>
                <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />
            </IconButton>
            <IconButton onClick={() => onCancel()}>
                <Icons.SmallIcon Icon={Icons.XClose} className="text-gray-400" />
            </IconButton>
        </form>
    )
}

const RadioButtonPlaceholder = () => {
    return (
        <div className="!cursor-not-allowed">
            <div className="pointer-events-none">
                <RadioButton isSelected={false} onClick={() => {}} />
            </div>
        </div>
    )
}

const Option = ({
    value,
    index,
    numOptions,
    onChange,
    onDelete,
    onMove,
    autoFocus,
}: {
    value: string
    index: number
    numOptions: number
    onChange: (val: string) => void
    onDelete: () => void
    onMove: (direction: 1 | -1) => void
    autoFocus: boolean
}) => {
    const [isEditing, setIsEditing] = useState(autoFocus)
    return (
        <div className="flex gap-2 items-center group/option">
            <RadioButtonPlaceholder />
            {isEditing ? (
                <OptionInput
                    initialValue={value}
                    onCancel={() => setIsEditing(false)}
                    onSubmit={(val) => {
                        onChange(val)
                        setIsEditing(false)
                    }}
                />
            ) : (
                <div className="flex items-center gap-3">
                    <h5
                        onClick={() => setIsEditing((prev) => !prev)}
                        className="text-white py-1.5 border-b border-b-transparent group-hover/option:border-b-gray-700 min-w-[111px]"
                    >
                        {value}
                    </h5>
                    <div className="flex items-center gap-2 opacity-0 group-hover/option:opacity-100 text-gray-100">
                        <IconButton className="hover:!text-red-500" onClick={() => onDelete()}>
                            <Icons.SmallIcon Icon={Icons.Trash} />
                        </IconButton>
                        <span className="flex items-center pl-2 border-l border-l-gray-700">
                            <IconButton onClick={() => onMove(-1)} disabled={index === 0}>
                                <Icons.Icon Icon={Icons.ChevronDown} />
                            </IconButton>
                            <IconButton
                                onClick={() => onMove(1)}
                                disabled={index + 1 === numOptions}
                            >
                                <Icons.Icon Icon={Icons.ChevronUp} />
                            </IconButton>
                        </span>
                    </div>
                </div>
            )}
        </div>
    )
}

const CustomMultipleChoiceField = ({
    spec,
    fieldControls,
    autoFocus = false,
}: FieldProps<"multiple-choice">) => {
    const [autofocusOptionIndex, setAutofocusOptionIndex] = useState<number>()

    const addOption = () => {
        fieldControls.editField(spec.id, {
            ...spec,
            options: [...spec.options, `Option ${spec.options.length + 1}`],
        })
        setAutofocusOptionIndex(spec.options.length)
    }

    const editOption = (index: number, value: string) => {
        fieldControls.editField(spec.id, {
            ...spec,
            options: spec.options.map((o, i) => (i === index ? value : o)),
        })
        setAutofocusOptionIndex(undefined)
    }

    const deleteOption = (index: number) => {
        fieldControls.editField(spec.id, {
            ...spec,
            options: spec.options.filter((_, i) => i !== index),
        })
    }

    const moveOption = (index: number, direction: -1 | 1) => {
        const element = spec.options[index]
        const copy = [...spec.options]
        copy.splice(index, 1)
        copy.splice(Math.max(index + direction, 0), 0, element)
        fieldControls.editField(spec.id, { ...spec, options: copy })
    }

    return (
        <div className="flex flex-col gap-2">
            <LabelInput
                value={spec.label}
                autoFocus={autoFocus}
                onChange={(label) => fieldControls.editField(spec.id, { ...spec, label })}
            />
            <div className="flex flex-col gap-1">
                {spec.options.map((option, i) => (
                    <Option
                        key={`${option}-${i}`}
                        index={i}
                        numOptions={spec.options.length}
                        value={option}
                        onChange={(val) => editOption(i, val)}
                        onDelete={() => deleteOption(i)}
                        onMove={(direction) => moveOption(i, direction)}
                        autoFocus={autofocusOptionIndex === i}
                    />
                ))}
            </div>
            <div className="flex gap-2 items-center cursor-pointer" onClick={addOption}>
                <RadioButtonPlaceholder />
                <h5 className="text-gray-400">Add option</h5>
            </div>
        </div>
    )
}

export const FieldBodyByType: { [T in CustomFieldType]: FunctionComponent<FieldProps<T>> } = {
    text: CustomTextField,
    "yes-no": CustomYesNoField,
    "multiple-choice": CustomMultipleChoiceField,
}

export { FieldFooter }
